import {
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ListItem,
  ListItemText,
  Icon,
} from "@mui/material";
import { forwardRef, useImperativeHandle, useState } from "react";
import MDBox from "theme/components/MDBox";
import FormSectionBuilder from "./form_section_builder";
import MDInput from "theme/components/MDInput";
import MDTypography from "theme/components/MDTypography";

const DataFormComponent = forwardRef(
  ({ agentRef, dataForm, onDraftDeleted }, ref) => {
    const [openSaveDialog, setOpenSaveDialog] = useState(false);
    const [openListDialog, setOpenListDialog] = useState(false);
    const [draftName, setDraftName] = useState("");
    const [drafts, setDrafts] = useState([]);
    const [savePromise, setSavePromise] = useState(null);
    const [selectedDraftIndex, setSelectedDraftIndex] = useState(null);

    function preprocessDataForm() {
      const dForm = document.querySelector(`#dataForm`);
      const formData = new FormData(dForm);
      const values = Object.fromEntries(formData.entries());

      let exludedName = "";
      let excluded = false;

      dataForm.form.form.sections.every((section) => {
        const ex = section.fields.every((field) => {
          if (!field.is_optional) {
            if (
              !formData.has(field.reference) ||
              values[field.reference]?.length <= 0
            ) {
              excluded = true;
              exludedName = `${field.display_name} (${section.title})`;
              return false;
            }
          }
          return true;
        });

        return ex;
      });

      if (excluded === true) {
        return { error: `${exludedName} is not provided.` };
      }

      return { values };
    }

    function handleSubmit() {
      if (dataForm === null) {
        return {
          error: "Data form not initialized.",
        };
      }

      return preprocessDataForm();
    }

    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      saveProgress: launch_save_progress,
      showSavedForms: show_saved_progresses,
      getSavedDraftsCount: get_saved_drafts_count,
      clearCurrentDraft: clear_current_draft,
    }));

    function getFormRef() {
      return `${agentRef}:${dataForm?.form?.reference || null}`;
    }

    function launch_save_progress() {
      return new Promise((resolve) => {
        if (selectedDraftIndex !== null && drafts[selectedDraftIndex]) {
          const existingName = drafts[selectedDraftIndex].name;

          const result = save_progress(existingName);
          resolve(result);
          return;
        }

        setDraftName("");
        setOpenSaveDialog(true);
        setSavePromise(() => resolve);
      });
    }

    function save_progress(name) {
      const dForm = document.querySelector(`#dataForm`);
      if (!dForm) return false;

      const formData = new FormData(dForm);
      const rawValues = Object.fromEntries(formData.entries());

      const values = Object.fromEntries(
        Object.entries(rawValues).filter(([_, value]) => {
          if (value === null || value === undefined) return false;
          if (typeof value === "string" && value.trim() === "") return false;
          return true;
        }),
      );

      if (Object.keys(values).length === 0) return false;

      const formRef = getFormRef();
      const STORAGE_KEY = `draft_${formRef}`;

      const saved = localStorage.getItem(STORAGE_KEY);
      let drafts = saved ? JSON.parse(saved) : [];

      const payload = {
        name: name || `Draft ${drafts.length + 1}`,
        values,
        last_updated: new Date().toISOString(),
      };

      if (selectedDraftIndex !== null && drafts[selectedDraftIndex]) {
        drafts[selectedDraftIndex] = payload;
      } else {
        drafts.push(payload);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));

      setOpenSaveDialog(false);
      savePromise?.(true);

      return true;
    }

    function show_saved_progresses() {
      const formRef = getFormRef();
      const key = `draft_${formRef}`;

      const saved = localStorage.getItem(key);
      let list = saved ? JSON.parse(saved) : [];

      // list = list.sort(
      //   (a, b) => new Date(b.last_updated) - new Date(a.last_updated),
      // );

      setDrafts(list);
      setOpenListDialog(true);
    }

    function load_saved_progress(index) {
      const draft = drafts[index];
      if (!draft) return;

      setSelectedDraftIndex(index);
      setOpenListDialog(false);
    }

    function get_saved_drafts_count() {
      const formRef = getFormRef();
      const key = `draft_${formRef}`;

      const saved = localStorage.getItem(key);
      if (!saved) return 0;

      try {
        const drafts = JSON.parse(saved);
        return Array.isArray(drafts) ? drafts.length : 0;
      } catch {
        return 0;
      }
    }

    function delete_saved_progress(index) {
      const formRef = getFormRef();
      const key = `draft_${formRef}`;

      const saved = localStorage.getItem(key);
      let drafts = saved ? JSON.parse(saved) : [];

      if (!drafts[index]) return;

      drafts.splice(index, 1);
      localStorage.setItem(key, JSON.stringify(drafts));

      // update UI
      setDrafts((prev) => prev.filter((_, i) => i !== index));

      // reset selection if needed
      if (selectedDraftIndex === index) {
        setSelectedDraftIndex(null);
      }

      setSelectedDraftIndex(Date.now());
      onDraftDeleted?.(index);
    }

    function clear_current_draft() {
      if (selectedDraftIndex === null || !drafts[selectedDraftIndex]) {
        return false;
      }

      delete_saved_progress(selectedDraftIndex);
      return true;
    }

    return (
      <MDBox>
        <Dialog open={openSaveDialog} onClose={() => setOpenSaveDialog(false)}>
          <DialogTitle>Save Form</DialogTitle>
          <DialogContent>
            <MDInput
              fullWidth
              placeholder="Enter name"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenSaveDialog(false);
                savePromise?.(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => save_progress(draftName)}>Save</Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openListDialog}
          onClose={() => setOpenListDialog(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Saved Progess</DialogTitle>
          <DialogContent>
            <MDBox>
              {drafts.length <= 0 ? (
                <MDTypography variant="body2">No saved progresses</MDTypography>
              ) : (
                <MDBox>
                  {drafts.map((d, i) => (
                    <MDBox mb={3}>
                      <ListItem divider button key={i}>
                        <ListItemText
                          primary={
                            <MDTypography variant="h6" fontWeight="regular">
                              {d.name}
                            </MDTypography>
                          }
                          secondary={
                            <MDTypography variant="body2">
                              {new Date(d.last_updated).toLocaleString()}
                            </MDTypography>
                          }
                          onClick={() => load_saved_progress(i)}
                        />
                        <Icon
                          color="error"
                          onClick={(e) => {
                            // e.stopPropagation();
                            delete_saved_progress(i);
                          }}
                        >
                          delete
                        </Icon>
                      </ListItem>
                    </MDBox>
                  ))}
                </MDBox>
              )}
            </MDBox>
          </DialogContent>
        </Dialog>
        {dataForm !== null && (
          <form id="dataForm" key={selectedDraftIndex ?? "default"}>
            {dataForm.form.form.sections.map((formSection) => {
              let sect = (
                <MDBox mb={3} key={formSection.reference}>
                  <Card sx={{ height: "100%", marginTop: "0px" }}>
                    <FormSectionBuilder
                      formRef={getFormRef()}
                      draftIndex={selectedDraftIndex}
                      section={formSection}
                      logic={dataForm.form.form.navigation_logic}
                    />
                  </Card>
                </MDBox>
              );

              if (
                formSection.reference in
                dataForm.form.form.navigation_logic.listeners
              ) {
                const listener =
                  dataForm.form.form.navigation_logic.listeners[
                    formSection.reference
                  ];

                const parentField = document.getElementById(
                  `dff_${listener.trigger}`,
                );

                if (parentField !== null) {
                  let wID = `t_watcher_${listener.type}_${formSection.reference}`;

                  sect = (
                    <div id={`${wID}`} style={{ display: "none" }} key={wID}>
                      {sect}
                    </div>
                  );

                  parentField.addEventListener("change", function () {
                    const d = document.getElementById(wID);

                    if (parentField.value !== listener.control) {
                      d.style.display = "none";
                      formSection.fields.every((fld) => {
                        const el = document.getElementById(
                          `dff_${fld.reference}`,
                        );
                        if (el) el.value = null;
                        return false;
                      });
                    } else {
                      d.style.display = "block";
                    }
                  });
                }
              }

              return sect;
            })}
          </form>
        )}
      </MDBox>
    );
  },
);

export default DataFormComponent;
